import type { AdamCommand, AdamCommandContext, AdamCommandResult } from "./Command";

/** Run several commands as a single undo/redo entry. */
export class AdamTransactionCommand implements AdamCommand {
  readonly id = "transaction";

  constructor(
    readonly label: string,
    private readonly commands: AdamCommand[],
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    const results = this.commands.map(command => command.execute(context));
    return mergeResults(results);
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    const results = [...this.commands].reverse().map(command => command.undo(context));
    return mergeResults(results);
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }

  canMergeWith(command: AdamCommand): boolean {
    return command instanceof AdamTransactionCommand
      && command.label === this.label
      && command.commands.length === this.commands.length
      && this.commands.every((entry, index) => {
        const next = command.commands[index];
        return Boolean(next && entry.canMergeWith?.(next) && entry.merge);
      });
  }

  merge(command: AdamCommand): AdamCommand {
    if (!(command instanceof AdamTransactionCommand) || !this.canMergeWith(command)) {
      return this;
    }
    return new AdamTransactionCommand(
      this.label,
      this.commands.map((entry, index) => entry.merge!(command.commands[index]!)),
    );
  }
}

function mergeResults(results: AdamCommandResult[]): AdamCommandResult {
  if (results.length === 0) {
    return { changed: "mixed" };
  }
  const kinds = new Set(results.map(r => r.changed));
  if (kinds.size === 1) {
    return { changed: results[0]!.changed };
  }
  return { changed: "mixed" };
}
